/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import { UnitDescribe } from 'convert-units';
import React from 'react';
import { Input, InputGroupText } from 'reactstrap';
import { BaseInputProps } from './InputProps';

const staticSuffix = (suffix: string, disabled?: boolean, suffixClass: string = '') => {
 return <InputGroupText className={`${suffixClass} ${disabled ? 'disabled' : ''}`}>{suffix}</InputGroupText>
}

interface inputGroupCurrencyProps extends Pick<BaseInputProps, 'isDisabled' | 'numberScale' | 'handleNumberScaleChange' | 'isInvalid'> {
  options: UnitDescribe[];
}

export function genericInputGroupCurrency({ isDisabled, numberScale, options, handleNumberScaleChange, isInvalid }: inputGroupCurrencyProps) {
  const disabled = isDisabled?.();
  const className = isInvalid ? 'styled-readonly-invalid' : 'styled-readonly';
  if (disabled) {
    if (numberScale) {
      const op = options.find((u) => u.abbr === numberScale);
      if (op) {
        return staticSuffix(op.plural, disabled, className);
      }
    }
    return staticSuffix('', disabled, className);
  }

  if (!numberScale) {
    return <></>;
  }

  return (
    <Input
      type='select'
      disabled={disabled}
      onChange={e => handleNumberScaleChange(e.target.value)}
      value={numberScale || ''}
      className={`${className} flex-grow-0 w-25`}>

      {options.map(u =>
        <option key={u.abbr} value={u.abbr}>
          {u.plural}
        </option>)
      }
    </Input>
  );
}

const getValueOptionText = (value: number | string | string[] | undefined, op: UnitDescribe) => Number(value) === 1 ? op.singular : op.plural;

export interface GenericInputGroupProps extends Pick<BaseInputProps, 'isDisabled' | 'unit' | 'suffix' | 'isInvalid'> {
  options?: UnitDescribe[],
  value?: number | string | string[],
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void,
}

export interface InputGroupProps extends GenericInputGroupProps, Pick<BaseInputProps, 'universalTracker' | 'numberScale' | 'handleNumberScaleChange' | 'isInvalid'> {
}

export function genericInputGroup({ options, value, isDisabled, unit, handleChange, suffix, isInvalid }: GenericInputGroupProps) {
  const disabled = isDisabled?.();
  const className = isInvalid ? 'styled-readonly-invalid' : 'styled-readonly';
  if (options && options.length > 0) {
    if (disabled) {
      if (unit) {
        const op = options.find((u) => u.abbr === unit);
        if (op) {
          return staticSuffix(getValueOptionText(value, op), disabled, className);
        }
      }
      return staticSuffix('', disabled, className);
    }

    return (
      <Input type='select' disabled={disabled} onChange={handleChange} value={unit || ''}
        className={`${className} flex-grow-0 w-25`}>
        {options.map(op => <option key={op.abbr} value={op.abbr}>
          {`${getValueOptionText(value, op)} (${op.abbr})`}
        </option>)}
      </Input>
    );
  }

  if (suffix) {
    return staticSuffix(suffix, disabled, className);
  }

  return '';
}

export function renderInputGroup(props: InputGroupProps) {

  const { universalTracker, suffix, unit, value, numberScale, handleChange, handleNumberScaleChange, isDisabled, isInvalid } = props;
  
  if (universalTracker.isCurrency()) {
    return genericInputGroupCurrency({
      isDisabled: isDisabled,
      numberScale: numberScale,
      options: universalTracker.getUnitOptions(),
      handleNumberScaleChange: handleNumberScaleChange,
      isInvalid,
    });
  }

  const options = universalTracker.getUnitOptions();
  return genericInputGroup({
    options: options,
    isDisabled: isDisabled,
    unit: unit,
    value: value,
    handleChange: handleChange,
    suffix: suffix,
    isInvalid,
  });
}
